import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, Image, TouchableOpacity, KeyboardAvoidingView, Platform} from 'react-native';
import {Text, Appbar, TextInput, Button, Snackbar, Menu, Chip, IconButton, RadioButton} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {launchImageLibrary} from 'react-native-image-picker';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import {useInventoryStore} from '../../store/useInventoryStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import {inventoryApi} from '../../services/api/inventoryApi';
import SelectSizeModal from './SelectSizeModal';
import {colors, spacing} from '../../theme';

const AddEditReadymadeScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const existingItem = route.params?.item;
  const defaultOrgUnitId = route.params?.defaultOrgUnitId;
  const isEdit = !!existingItem;

  const {createReadymade, updateReadymade, uploadReadymadeImages} = useInventoryCatalogStore();
  const {readymadeTypes, readymadeSizes, fetchReadymadeTypes, fetchReadymadeSizes} = useInventoryStore();
  const {orgUnits, fetchOrgUnits} = useOrgUnitStore();

  const [name, setName] = useState(existingItem?.name || '');
  const [readymadeTypeId, setReadymadeTypeId] = useState(existingItem?.readymadeTypeId || null);
  const [sku, setSku] = useState(existingItem?.sku || '');
  const [selectedSizeIds, setSelectedSizeIds] = useState(existingItem?.sizeIds || []);
  const [sellingPrice, setSellingPrice] = useState(existingItem?.sellingPrice != null ? String(existingItem.sellingPrice) : '');
  const [purchasePrice, setPurchasePrice] = useState(existingItem?.purchasePrice != null ? String(existingItem.purchasePrice) : '');
  const [currentStock, setCurrentStock] = useState(existingItem?.currentStock != null ? String(existingItem.currentStock) : '0');
  const [orgUnitId, setOrgUnitId] = useState(defaultOrgUnitId || null);

  const [typeMenuVisible, setTypeMenuVisible] = useState(false);
  const [sizeModalVisible, setSizeModalVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Image state
  const [mainImageFile, setMainImageFile] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState(existingItem?.mainImageUrl || null);
  const [additionalSlots, setAdditionalSlots] = useState(() => {
    if (existingItem?.additionalImageUrls?.length) {
      return existingItem.additionalImageUrls.map(uri => ({uri}));
    }
    return [];
  });
  const [imagesChanged, setImagesChanged] = useState(false);

  useEffect(() => {
    fetchReadymadeTypes();
    fetchReadymadeSizes();
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [fetchReadymadeTypes, fetchReadymadeSizes, fetchOrgUnits, orgUnits.length]);

  // Auto-select if only one store
  useEffect(() => {
    if (!isEdit && !orgUnitId && orgUnits.length === 1) {
      setOrgUnitId(orgUnits[0].id);
    }
  }, [orgUnits, isEdit, orgUnitId]);

  // Fetch fresh data when editing to get latest image URLs
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    inventoryApi.readymades.get(existingItem.id).then(res => {
      if (cancelled) return;
      const fresh = res.data.data;
      if (fresh.mainImageUrl) {
        setMainImagePreview(prev => prev || fresh.mainImageUrl);
      }
      if (fresh.additionalImageUrls?.length) {
        setAdditionalSlots(prev =>
          prev.length > 0 ? prev : fresh.additionalImageUrls.map(uri => ({uri})),
        );
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit, existingItem?.id]);

  const selectedTypeName = readymadeTypes.find(t => t.id === readymadeTypeId)?.name || t('inventory.readymadeType');
  const selectedSizeNames = selectedSizeIds.map(id => readymadeSizes.find(s => s.id === id)?.name).filter(Boolean);

  const pickImage = useCallback(async (onPick) => {
    const result = await launchImageLibrary({
      mediaType: 'photo',
      maxWidth: 1024,
      maxHeight: 1024,
      quality: 0.8,
    });
    if (!result.didCancel && result.assets?.[0]) {
      onPick(result.assets[0]);
      setImagesChanged(true);
    }
  }, []);

  const pickMainImage = useCallback(() => {
    pickImage(asset => {
      setMainImageFile(asset);
      setMainImagePreview(asset.uri);
    });
  }, [pickImage]);

  const removeMainImage = useCallback(() => {
    setMainImageFile(null);
    setMainImagePreview(null);
    setImagesChanged(true);
  }, []);

  const pickAdditionalImage = useCallback(() => {
    pickImage(asset => {
      setAdditionalSlots(prev => [...prev, {file: asset, uri: asset.uri}]);
    });
  }, [pickImage]);

  const removeAdditionalImage = useCallback((index) => {
    setAdditionalSlots(prev => prev.filter((_, i) => i !== index));
    setImagesChanged(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!name.trim() || !readymadeTypeId) {
      setSnackbar(t('validation.required'));
      return;
    }
    if (!isEdit && !orgUnitId) {
      setSnackbar(t('inventory.storeRequired'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        readymadeTypeId,
        sku: sku.trim() || null,
        sizeIds: selectedSizeIds,
        sellingPrice: sellingPrice ? parseFloat(sellingPrice) : null,
      };

      let readymadeId;
      if (isEdit) {
        await updateReadymade(existingItem.id, payload);
        readymadeId = existingItem.id;
      } else {
        payload.purchasePrice = purchasePrice ? parseFloat(purchasePrice) : null;
        payload.currentStock = currentStock ? parseFloat(currentStock) : 0;
        payload.orgUnitId = orgUnitId;
        const created = await createReadymade(payload);
        readymadeId = created.id;
      }

      if (imagesChanged) {
        await uploadReadymadeImages(readymadeId, mainImageFile, additionalSlots);
      }

      navigation.goBack();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [name, readymadeTypeId, sku, selectedSizeIds, sellingPrice, purchasePrice, currentStock, orgUnitId, isEdit, existingItem, createReadymade, updateReadymade, uploadReadymadeImages, mainImageFile, additionalSlots, imagesChanged, navigation, t]);

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar} dark>
        <Appbar.BackAction onPress={() => navigation.goBack()} />
        <Appbar.Content title={isEdit ? t('inventory.editReadymade') : t('inventory.addReadymade')} />
      </Appbar.Header>

      <KeyboardAvoidingView
        style={{flex: 1}}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView style={styles.form} keyboardShouldPersistTaps="handled">
        {/* Store Selection - only for new readymades */}
        {!isEdit && orgUnits.length > 0 && (
          <View style={styles.storeSection}>
            <Text style={styles.storeLabel}>{t('inventory.store')} *</Text>
            <View style={styles.storeOptions}>
              {orgUnits.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[styles.storeChip, orgUnitId === store.id && styles.storeChipSelected]}
                  onPress={() => setOrgUnitId(store.id)}>
                  <RadioButton.Android
                    value={String(store.id)}
                    status={orgUnitId === store.id ? 'checked' : 'unchecked'}
                    onPress={() => setOrgUnitId(store.id)}
                    color={colors.primary}
                  />
                  <View style={styles.storeChipContent}>
                    <Text style={[styles.storeChipText, orgUnitId === store.id && styles.storeChipTextSelected]}>
                      {store.name}
                    </Text>
                    {store.city && <Text style={styles.storeChipCity}>{store.city}</Text>}
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        <TextInput
          label={t('inventory.readymadeName')}
          value={name}
          onChangeText={setName}
          mode="outlined"
          style={styles.input}
        />

        <Menu
          visible={typeMenuVisible}
          onDismiss={() => setTypeMenuVisible(false)}
          anchor={
            <TextInput
              label={t('inventory.readymadeType')}
              value={selectedTypeName}
              mode="outlined"
              style={styles.input}
              right={<TextInput.Icon icon="menu-down" onPress={() => setTypeMenuVisible(true)} />}
              onPressIn={() => setTypeMenuVisible(true)}
              editable={false}
            />
          }>
          {readymadeTypes.map(rt => (
            <Menu.Item
              key={rt.id}
              title={rt.name}
              onPress={() => { setReadymadeTypeId(rt.id); setTypeMenuVisible(false); }}
            />
          ))}
        </Menu>

        <TextInput
          label={t('inventory.sku')}
          value={sku}
          onChangeText={setSku}
          mode="outlined"
          style={styles.input}
        />

        <Button
          mode="outlined"
          onPress={() => setSizeModalVisible(true)}
          style={styles.sizeButton}
          icon="resize">
          {t('inventory.selectSizes')}
        </Button>

        {selectedSizeNames.length > 0 && (
          <View style={styles.sizeChips}>
            {selectedSizeNames.map((name, idx) => (
              <Chip key={idx} style={styles.sizeChip}><Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{name}</Text></Chip>
            ))}
          </View>
        )}

        <TextInput
          label={t('inventory.sellingPrice')}
          value={sellingPrice}
          onChangeText={setSellingPrice}
          keyboardType="decimal-pad"
          mode="outlined"
          left={<TextInput.Affix text="₹" />}
          style={styles.input}
        />

        <TextInput
          label={t('inventory.purchasePrice')}
          value={purchasePrice}
          onChangeText={isEdit ? undefined : setPurchasePrice}
          keyboardType="decimal-pad"
          mode="outlined"
          left={<TextInput.Affix text="₹" />}
          style={styles.input}
          disabled={isEdit}
        />
        {isEdit && (
          <Text style={styles.hintText}>{t('inventory.purchasePriceReadonly')}</Text>
        )}

        <TextInput
          label={t('inventory.currentStock')}
          value={currentStock}
          onChangeText={isEdit ? undefined : setCurrentStock}
          keyboardType="decimal-pad"
          mode="outlined"
          style={styles.input}
          disabled={isEdit}
        />
        {isEdit && (
          <Text style={styles.hintText}>{t('inventory.stockReadonly')}</Text>
        )}

        {/* Images Section */}
        <Text style={styles.sectionHeader}>{t('inventory.mainImage')}</Text>
        <TouchableOpacity style={styles.mainImageBlock} onPress={pickMainImage} activeOpacity={0.7}>
          {mainImagePreview ? (
            <View style={styles.imagePreviewContainer}>
              <Image source={{uri: mainImagePreview}} style={styles.mainImagePreview} />
              <IconButton
                icon="close-circle"
                size={22}
                iconColor={colors.error}
                style={styles.removeImageBtn}
                onPress={removeMainImage}
              />
            </View>
          ) : (
            <View style={styles.imagePlaceholder}>
              <Icon name="camera-plus-outline" size={32} color={colors.textSecondary} />
              <Text style={styles.uploadText}>{t('inventory.tapToUpload')}</Text>
            </View>
          )}
        </TouchableOpacity>

        <Text style={styles.sectionHeader}>{t('inventory.additionalImages')}</Text>
        <View style={styles.additionalRow}>
          {additionalSlots.map((slot, index) => (
            <View key={index} style={styles.additionalSlot}>
              <Image source={{uri: slot.uri}} style={styles.additionalImage} />
              <IconButton
                icon="close-circle"
                size={20}
                iconColor={colors.error}
                style={styles.removeAdditionalBtn}
                onPress={() => removeAdditionalImage(index)}
              />
            </View>
          ))}
          {additionalSlots.length < 3 && (
            <TouchableOpacity style={styles.additionalAddBlock} onPress={pickAdditionalImage} activeOpacity={0.7}>
              <Icon name="plus" size={28} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving}
          style={styles.saveButton}>
          {t('common.save')}
        </Button>
      </ScrollView>
      </KeyboardAvoidingView>

      <SelectSizeModal
        visible={sizeModalVisible}
        selectedIds={selectedSizeIds}
        onDismiss={() => setSizeModalVisible(false)}
        onSave={(ids) => { setSelectedSizeIds(ids); setSizeModalVisible(false); }}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const IMAGE_SIZE = 90;

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  appbar: {backgroundColor: colors.primary},
  form: {flex: 1, padding: spacing.md},
  storeSection: {
    marginBottom: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  storeLabel: {fontSize: 14, fontWeight: '600', color: colors.text, marginBottom: spacing.sm},
  storeOptions: {gap: spacing.xs},
  storeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingRight: spacing.sm,
  },
  storeChipSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '10',
  },
  storeChipContent: {flex: 1},
  storeChipText: {fontSize: 14, color: colors.text},
  storeChipTextSelected: {color: colors.primary, fontWeight: '500'},
  storeChipCity: {fontSize: 12, color: colors.textSecondary},
  input: {marginBottom: spacing.sm},
  hintText: {fontSize: 12, color: colors.textSecondary, marginBottom: spacing.sm, marginTop: -4, paddingLeft: spacing.xs},
  sizeButton: {marginBottom: spacing.sm},
  sizeChips: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm},
  sizeChip: {backgroundColor: colors.primary + '15'},
  sectionHeader: {fontSize: 14, fontWeight: '600', color: colors.text, marginTop: spacing.md, marginBottom: spacing.sm},
  mainImageBlock: {
    width: '100%',
    height: 160,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.textSecondary,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: spacing.sm,
  },
  imagePreviewContainer: {flex: 1},
  mainImagePreview: {width: '100%', height: '100%', resizeMode: 'cover'},
  removeImageBtn: {position: 'absolute', top: 0, right: 0, backgroundColor: 'rgba(255,255,255,0.8)', margin: 4},
  imagePlaceholder: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  uploadText: {fontSize: 12, color: colors.textSecondary, marginTop: 4},
  additionalRow: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginBottom: spacing.sm},
  additionalSlot: {width: IMAGE_SIZE, height: IMAGE_SIZE, borderRadius: 8, overflow: 'hidden'},
  additionalImage: {width: '100%', height: '100%', resizeMode: 'cover'},
  removeAdditionalBtn: {position: 'absolute', top: -4, right: -4, backgroundColor: 'rgba(255,255,255,0.8)', margin: 0},
  additionalAddBlock: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.textSecondary,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveButton: {marginTop: spacing.md, marginBottom: spacing.xl},
});

export default AddEditReadymadeScreen;
